import { GoogleGenerativeAI } from '@google/generative-ai';

const API_KEY = import.meta.env.VITE_GEMINI_API_KEY;

export async function extractBusinessCardInfo(base64Image) {
  if (!API_KEY) {
    throw new Error("ไม่พบ API Key กรุณาตั้งค่า VITE_GEMINI_API_KEY ในไฟล์ .env");
  }

  const genAI = new GoogleGenerativeAI(API_KEY);
  const model = genAI.getGenerativeModel({ model: "gemini-flash-lite-latest" });

  try {
    // Determine mimeType from the base64 string
    const matches = base64Image.match(/^data:(image\/[a-zA-Z+]+);base64,(.+)$/);
    let mimeType = 'image/jpeg';
    let base64Data = base64Image;

    if (matches && matches.length === 3) {
      mimeType = matches[1];
      base64Data = matches[2];
    }

    const prompt = `ดึงข้อมูลจากรูปนามบัตรนี้ แล้วส่งค่ากลับมาเป็นรูปแบบ JSON เท่านั้น (ห้ามมี Markdown หรือคำอธิบายเพิ่มเติมใดๆ). 
ถ้าอ่านไม่ออกหรือไม่มีข้อมูล ให้ใส่ค่าเป็น "".
รูปแบบที่ต้องการ:
{
  "name": "ชื่อ-นามสกุล",
  "job_title": "ตำแหน่งงาน",
  "company": "ชื่อบริษัท หรือองค์กร",
  "phone": "เบอร์โทรศัพท์ทั้งหมดที่มี",
  "email": "อีเมล",
  "address": "ที่อยู่ (ให้เริ่มต้นที่บ้านเลขที่ หรือเลขที่ตั้งบริษัทเป็นต้นไป ตัดชื่อสาขา/แผนกที่อยู่ข้างหน้าออก)"
}`;

    const imagePart = {
      inlineData: {
        data: base64Data,
        mimeType: mimeType
      }
    };

    let result;
    let retries = 3;
    let delay = 1500;

    for (let i = 0; i < retries; i++) {
      try {
        result = await model.generateContent([prompt, imagePart]);
        break; // Success
      } catch (err) {
        let errorMsg = err.message || String(err);
        if (errorMsg.includes('503') || errorMsg.toLowerCase().includes('high demand') || errorMsg.toLowerCase().includes('overloaded')) {
          if (i === retries - 1) throw err; // Throw on last retry
          console.warn(`Gemini API busy (503). Retrying ${i + 1}/${retries - 1} in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          delay *= 1.5; // Exponential backoff
        } else {
          throw err; // Not a 503 error, throw immediately
        }
      }
    }
    const response = await result.response;
    const textResponse = response.text();
    if (!textResponse) throw new Error("ไม่ได้รับข้อมูลตอบกลับจาก AI");

    // Clean up potential markdown formatting (```json ... ```)
    const cleanedText = textResponse.replace(/```json/g, '').replace(/```/g, '').trim();
    
    try {
      const json = JSON.parse(cleanedText);
      return json;
    } catch (parseError) {
      console.error("JSON Parse Error:", parseError, "Raw output:", cleanedText);
      throw new Error("AI ตอบกลับมาในรูปแบบที่ไม่ถูกต้อง");
    }

  } catch (error) {
    console.error("Gemini API Error:", error);
    let errorMsg = error.message || String(error);
    
    if (errorMsg.includes('503') || errorMsg.toLowerCase().includes('high demand') || errorMsg.toLowerCase().includes('overloaded')) {
      throw new Error('เซิร์ฟเวอร์ AI มีผู้ใช้งานจำนวนมากชั่วคราว กรุณารอสักครู่แล้วลองใหม่อีกครั้งครับ');
    } else if (errorMsg.includes('429') || errorMsg.toLowerCase().includes('quota') || errorMsg.toLowerCase().includes('exhausted')) {
      throw new Error('โควต้าการใช้งาน AI เต็มแล้ว กรุณาตรวจสอบ API Key ของคุณครับ');
    } else if (errorMsg.includes('404') || errorMsg.toLowerCase().includes('not found')) {
      throw new Error('ไม่พบโมเดล AI ที่ระบุ หรือมีการเปลี่ยนแปลงเวอร์ชั่นของ AI ครับ');
    } else if (errorMsg.includes('400') || errorMsg.toLowerCase().includes('api key not valid')) {
      throw new Error('API Key ไม่ถูกต้อง กรุณาตรวจสอบการตั้งค่า API Key ครับ');
    }
    
    throw new Error(errorMsg);
  }
}

export async function startAIChat(user) {
  if (!API_KEY) {
    throw new Error("ไม่พบ API Key");
  }

  const genAI = new GoogleGenerativeAI(API_KEY);
  
  // Define the tools the AI can use to fetch data
  const tools = [{
    functionDeclarations: [
      {
        name: "getLeadsData",
        description: "ดึงข้อมูลลูกค้าเป้าหมาย (Leads) ของทีม สามารถกรองตามจังหวัดหรือสถานะได้",
        parameters: {
          type: "OBJECT",
          properties: {
            province: {
              type: "STRING",
              description: "ชื่อจังหวัด (ภาษาไทย) เช่น ชลบุรี, ระยอง (ใส่ค่าว่างถ้าไม่ต้องการกรอง)"
            },
            status: {
              type: "STRING",
              description: "สถานะของ Lead เช่น New, Contacted, Qualified (ใส่ค่าว่างถ้าไม่ต้องการกรอง)"
            }
          }
        }
      },
      {
        name: "getCustomersData",
        description: "ดึงข้อมูลลูกค้าปัจจุบัน (Customers) ในระบบ สามารถกรองตามจังหวัดได้",
        parameters: {
          type: "OBJECT",
          properties: {
            province: {
              type: "STRING",
              description: "ชื่อจังหวัด (ภาษาไทย) เช่น ชลบุรี, ระยอง (ใส่ค่าว่างถ้าไม่ต้องการกรอง)"
            }
          }
        }
      }
    ]
  }];

  const model = genAI.getGenerativeModel({ 
    model: "gemini-flash-lite-latest",
    tools: tools
  });

  const systemInstruction = `คุณคือ AI ผู้ช่วยอัจฉริยะสำหรับแอปพลิเคชัน "Salesvigator" 
คุณกำลังคุยกับผู้ใช้งานชื่อ: ${user?.name || user?.google_email} 
ตำแหน่ง: ${user?.role} 
ทีม: ${user?.team_name || 'ไม่ระบุ'}

หน้าที่ของคุณคือช่วยตอบคำถามและให้ข้อมูลเกี่ยวกับระบบ Salesvigator คุณสามารถใช้เครื่องมือ (Tools) ในการดึงข้อมูลจากระบบฐานข้อมูลได้หากผู้ใช้ถามถึงข้อมูลลูกค้าหรือ Leads
ตัวอย่างการใช้งาน:
- ถ้าผู้ใช้ถาม "มีลูกค้าใหม่กี่คนในชลบุรี" ให้เรียกใช้ getLeadsData พร้อมส่ง province="ชลบุรี" และ status="New"
- ถ้าผู้ใช้ถาม "เรามีลูกค้าในระยองไหม" ให้เรียกใช้ getCustomersData พร้อมส่ง province="ระยอง"

กฎสำคัญ 1: เมื่อคุณได้รับผลลัพธ์จากการเรียกใช้ Tool แล้ว คุณ **ต้อง** นำผลลัพธ์นั้นมาสรุปและพิมพ์ตอบกลับผู้ใช้งานเสมอ ห้ามตอบกลับเป็นข้อความว่างเปล่าเด็ดขาด
กฎสำคัญ 2: ในผลลัพธ์ของ Tool จะมีการส่งตัวอย่างรายชื่อบริษัทมาในรูปแบบ Markdown Link เช่น [บริษัท เอบีซี จำกัด](customer_123) คุณ **ต้อง** คัดลอกรูปแบบลิงก์เหล่านั้นมาใส่ในคำตอบของคุณด้วยรูปแบบเดิมทุกตัวอักษร ห้ามนำลิงก์ออกหรือเอามาแค่ชื่อเด็ดขาด เพื่อให้หน้าจอแชทสามารถสร้างปุ่มให้ผู้ใช้คลิกดูพิกัดได้

กรุณาตอบเป็นภาษาไทยอย่างสุภาพและเป็นมืออาชีพ`;

  const chat = model.startChat({
    systemInstruction: { role: "system", parts: [{ text: systemInstruction }] },
    history: []
  });

  return chat;
}

export async function sendMessageToAI(chat, messageText, user) {
  try {
    const result = await chat.sendMessage(messageText);
    
    // Check if the AI decided to call a function
    const calls = result.response.functionCalls ? result.response.functionCalls() : null;
    
    if (calls && calls.length > 0) {
      const call = calls[0];
      let apiResponse = null;
      
      try {
        if (call.name === "getLeadsData") {
          const url = `/api/leads?email=${encodeURIComponent(user.google_email)}&role=${user.role}&team_name=${encodeURIComponent(user.team_name || '')}`;
          const res = await fetch(url);
          const data = await res.json();
          
          if (data.status === 'success') {
            let leads = data.data;
            if (call.args && call.args.province) {
              leads = leads.filter(l => l.province && l.province.includes(call.args.province));
            }
            if (call.args && call.args.status) {
              leads = leads.filter(l => l.lead_status && l.lead_status.toLowerCase() === call.args.status.toLowerCase());
            }
            
            apiResponse = {
              count: leads.length,
              summary: leads.length > 0 ? `พบ ${leads.length} รายการ. รายชื่อบริษัท: ${leads.slice(0, 50).map(l => `[${l.company_name_th || l.company_name_en}](lead_${l.id})`).join(', ')}` : "ไม่พบข้อมูล"
            };
          }
        } else if (call.name === "getCustomersData") {
          const url = `/api/customers?email=${encodeURIComponent(user.google_email)}&role=${user.role}&team_name=${encodeURIComponent(user.team_name || '')}`;
          const res = await fetch(url);
          const data = await res.json();
          
          if (data.status === 'success') {
            let customers = data.data;
            if (call.args && call.args.province) {
              customers = customers.filter(c => c.address && c.address.includes(call.args.province));
            }
            
            apiResponse = {
              count: customers.length,
              summary: customers.length > 0 ? `พบ ${customers.length} รายการ. รายชื่อลูกค้า: ${customers.slice(0, 50).map(c => `[${c.customer_name}](customer_${c.id})`).join(', ')}` : "ไม่พบข้อมูล"
            };
          }
        }
      } catch (e) {
        console.error("Tool execution error:", e);
        apiResponse = { error: "Failed to execute tool" };
      }

      // Send the function response back to the AI
      const secondResult = await chat.sendMessage([{
        functionResponse: {
          name: call.name,
          response: apiResponse || { error: "No data found" }
        }
      }]);
      
      const finalReply = secondResult.response.text();
      if (!finalReply || finalReply.trim() === '') {
        return apiResponse?.summary || "ดึงข้อมูลเรียบร้อยแล้วค่ะ";
      }
      return finalReply;
    }
    
    return result.response.text();
  } catch (err) {
    console.error("AI Chat Error:", err);
    let errorMsg = err.message || String(err);
    if (errorMsg.includes('429') || errorMsg.toLowerCase().includes('quota') || errorMsg.toLowerCase().includes('exhausted')) {
      return "ขออภัยค่ะ โควต้าการใช้งาน API ของคุณเต็มแล้ว กรุณาตรวจสอบแผนการใช้งานและการตั้งค่าการชำระเงินที่ Google AI Studio ค่ะ";
    }
    if (errorMsg.includes('503') || errorMsg.toLowerCase().includes('overloaded') || errorMsg.toLowerCase().includes('high demand')) {
      return "ขออภัยค่ะ ขณะนี้เซิร์ฟเวอร์ AI มีผู้ใช้งานหนาแน่นมาก (503 Server Overloaded) กรุณารอสักครู่แล้วลองใหม่อีกครั้งค่ะ";
    }
    return "ขออภัยค่ะ ฉันพบปัญหาในการประมวลผลคำถามของคุณในขณะนี้ กรุณาลองใหม่อีกครั้งค่ะ (" + errorMsg + ")";
  }
}
